import {
  CustomizeFormValues,
  initialCustomizeFormValues,
} from '@/models/customize-form-schemas'
import { apiRequest } from '../lib/api-client'
import type {
  AgencyMembership,
  AuthUser,
  UserProfile,
} from '../stores/auth-store'
import {
  updateAuthUser,
  type RedactionPreferences,
} from '@rh/shared/stores/auth-store'
import {
  applyDesignBrandColor,
  DEFAULT_DESIGN,
} from '@rh/shared/templates/default-design'
import {
  Agency,
  AgencyService,
  UpdateAgencyPreferencesRequest,
} from './agency-service'

export interface UpdateCurrentUserRequest {
  fullName?: string
}

export interface UpdateCurrentUserResponse {
  user: AuthUser
  message?: string
}

export interface GetCurrentUserResponse {
  user: AuthUser
  agencyContext?: {
    agency: {
      id: string
      name: string
      plan: string
    }
    userRole: string
  }
}

export interface GetAgencyProfileResponse {
  agencyProfile: Agency
  recruiterProfile?: UserProfile
  membership: AgencyMembership
}

export interface UpdateRedactionPreferencesRequest {
  redactionPreferences: RedactionPreferences
}

export interface GetRedactionPreferencesResponse {
  redactionPreferences: RedactionPreferences | null
  hasRedactionPreferences: boolean
}

async function getCurrentUser(): Promise<GetCurrentUserResponse> {
  return apiRequest<GetCurrentUserResponse>('/users/me', {
    method: 'GET',
  })
}

async function updateCurrentUser(
  payload: UpdateCurrentUserRequest
): Promise<UpdateCurrentUserResponse> {
  return apiRequest<UpdateCurrentUserResponse>('/users/me', {
    method: 'PUT',

    json: payload,
  })
}

async function updateAgencyProfile(
  agencyId: string,
  profile: UserProfile,
  options: { abortSignal?: AbortSignal } = {}
): Promise<UpdateCurrentUserResponse> {
  const response = await apiRequest<UpdateCurrentUserResponse>(
    `/users/me/agency-profile/${agencyId}`,
    {
      method: 'PUT',

      json: profile,
      signal: options.abortSignal,
    }
  )

  const latestUser = response.user

  updateAuthUser({
    id: latestUser.id,
    email: latestUser.email,
    fullName: latestUser.fullName,
    appRole: latestUser.appRole,
    emailVerified: latestUser.emailVerified,
    createdAt: latestUser.createdAt,
    updatedAt: latestUser.updatedAt,
  })

  return response
}

const optional = (value?: string | null) => {
  if (value == null) {
    return undefined
  }

  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : undefined
}

async function onboardToAgency(data: {
  recruiterName?: string | undefined
  recruiterEmail?: string | undefined
  recruiterPhoneNumber?: string | undefined
  recruiterLinkedIn?: string | undefined
  agencyName?: string | undefined
  agencyPhoneNumber?: string | undefined
  agencyUrl?: string | undefined
  agencyLinkedIn?: string | undefined
  agencyEmail?: string | undefined
  agencyBrandColor?: string | undefined
  abortSignal?: AbortSignal
}) {
  const agencyPhone = optional(data.agencyPhoneNumber)
  const agencyUrl = optional(data.agencyUrl)
  const agencyLinkedIn = optional(data.agencyLinkedIn)
  const agencyEmail = optional(data.agencyEmail)
  const agencyBrandColor = optional(data.agencyBrandColor) ?? '#000000'
  const agencyName = data.agencyName?.trim() ?? ''
  const recruiterName = data.recruiterName?.trim() ?? ''

  if (agencyName.length === 0) {
    throw new Error('Agency name is required')
  }
  if (recruiterName.length === 0) {
    throw new Error('Recruiter name is required')
  }

  // Build customize preferences for agency - store agency details directly in header/footer (T042)
  const customizePreferences: CustomizeFormValues = {
    ...initialCustomizeFormValues,
    header: {
      ...initialCustomizeFormValues.header,
      agencyName,
      agencyDetails: {
        ...initialCustomizeFormValues.header.agencyDetails,
        phoneNumber: agencyPhone,
        url: agencyUrl,
        linkedIn: agencyLinkedIn,
        emailAddress: agencyEmail,
      },
      recruiterDetails: {
        ...initialCustomizeFormValues.header.recruiterDetails,
      },
    },
    footer: {
      ...initialCustomizeFormValues.footer,
      agencyName,
      agencyDetails: {
        ...initialCustomizeFormValues.footer.agencyDetails,
        phoneNumber: agencyPhone,
        url: agencyUrl,
        linkedIn: agencyLinkedIn,
        emailAddress: agencyEmail,
      },
      recruiterDetails: {
        ...initialCustomizeFormValues.footer.recruiterDetails,
      },
    },
    watermark: {
      ...initialCustomizeFormValues.watermark,
    },
  }

  if (agencyBrandColor) {
    customizePreferences.borderColor = agencyBrandColor
  }

  // Agency identity is stored at Agency.name level and agency details in
  // customize.header/footer.agencyDetails (T044). The customize seed stays
  // load-bearing: it is where `{{agency.*}}` design tokens resolve from.
  //
  // New agencies are also seeded with the stock DEFAULT_DESIGN (brand color
  // substituted in) so the Customize editor opens on the same design the
  // trial page showed, and future default-design changes never re-skin
  // agencies created before them. This is a fallback: when an admin has
  // uploaded a default design (admin console → Default design), the onboard
  // endpoint replaces this seed server-side with that design instead.
  const agencyPreferences: UpdateAgencyPreferencesRequest = {
    customize: customizePreferences,
    design: applyDesignBrandColor(DEFAULT_DESIGN, agencyBrandColor),
  }

  // Build recruiter profile
  const recruiterProfile = {
    name: recruiterName,
    ...(data.recruiterPhoneNumber ? { phone: data.recruiterPhoneNumber } : {}),
    ...(data.recruiterEmail ? { email: data.recruiterEmail } : {}),
    ...(data.recruiterLinkedIn ? { linkedIn: data.recruiterLinkedIn } : {}),
  }

  // Call new idempotent onboarding endpoint
  const response = await apiRequest<{
    agency: {
      id: string
      name: string
      plan: string
      createdAt: string
    }
    isNewAgency: boolean
    message: string
  }>('/users/me/onboard', {
    method: 'POST',

    json: {
      agencyName,
      agencyPreferences,
      recruiterProfile,
    },
    signal: data.abortSignal,
  })

  // Reload agencies to get fresh data
  await AgencyService.reloadUserAgencies({ abortSignal: data.abortSignal })

  // Refresh user data to get updated fullName (set by server during onboarding)
  const freshUser = await getCurrentUser()
  updateAuthUser({
    id: freshUser.user.id,
    email: freshUser.user.email,
    fullName: freshUser.user.fullName,
    appRole: freshUser.user.appRole,
    emailVerified: freshUser.user.emailVerified,
    createdAt: freshUser.user.createdAt,
    updatedAt: freshUser.user.updatedAt,
  })

  return {
    agencyName: response.agency.name,
    agencyId: response.agency.id,
    message: response.message,
    isNewAgency: response.isNewAgency,
  }
}

async function getAgencyProfile(
  agencyId: string,
  options: { abortSignal?: AbortSignal } = {}
): Promise<GetAgencyProfileResponse> {
  return apiRequest<GetAgencyProfileResponse>(
    `/users/me/agency-profile/${agencyId}`,
    {
      method: 'GET',

      signal: options.abortSignal,
    }
  )
}

async function getRedactionPreferences(
  agencyId: string
): Promise<GetRedactionPreferencesResponse> {
  return apiRequest<GetRedactionPreferencesResponse>(
    `/users/me/agency-profile/${agencyId}/redaction-preferences`,
    {
      method: 'GET',
    }
  )
}

async function updateRedactionPreferences(
  agencyId: string,
  preferences: RedactionPreferences
): Promise<UpdateCurrentUserResponse> {
  return apiRequest<UpdateCurrentUserResponse>(
    `/users/me/agency-profile/${agencyId}/redaction-preferences`,
    {
      method: 'PUT',

      json: { redactionPreferences: preferences },
    }
  )
}

export const UserService = {
  getCurrentUser,
  updateCurrentUser,
  updateAgencyProfile,
  getAgencyProfile,
  getRedactionPreferences,
  onboardToAgency,
  updateRedactionPreferences,
}
